import { AgGridReact } from "ag-grid-react";
import { useMemo } from "react";
import { AllCommunityModule, ColDef, ModuleRegistry } from "ag-grid-community";
import { StatusBarModule } from "ag-grid-enterprise";
import clsx from "clsx";
import { LicenseManager } from "ag-grid-enterprise";
import { investmentMemoGridTheme } from "./agGridTheme";
import RightSidebarButton from "@/features/Navigation/components/RightSidebarButton";
import { FinanceLabField, FinanceLabMetric } from "@/entities/FinanceLab";
import { useFlags } from "@/utils/flagsmith/useFlags";

LicenseManager.setLicenseKey(process.env.AG_LICENSE_KEY || "");

ModuleRegistry.registerModules([AllCommunityModule, StatusBarModule]);

interface FinanceLabMetricTableProps {
    metric: FinanceLabMetric | undefined;
    title: string;
}

export function FinanceLabMetricTable({
    metric,
    title,
}: FinanceLabMetricTableProps) {
    const flags = useFlags();
    const showDataSources = flags.enabled("show-data-sources");

    const label = useMemo(() => {
        return metric?.info?.derived?.label || null;
    }, [metric?.info]);

    const isRecurringPayment = useMemo(() => {
        return label === FinanceLabField.DEAL_RECURRING_PAYMENT;
    }, [label]);

    const columnDefs = useMemo<ColDef[]>(() => {
        return [
            {
                field: "period",
                resizable: false,
                flex: 1,
                wrapText: true,
                autoHeight: true,
            },
            {
                field: "value",
                resizable: false,
                flex: 2,
                pinned: "right",
                wrapText: true,
                autoHeight: true,
                valueFormatter: ({ data }) => {
                    return data?.formattedValue || "";
                },
            },
        ];
    }, []);

    const defaultColDef = useMemo<ColDef>(() => {
        return {
            flex: 1,
        };
    }, []);

    const statusBar = useMemo(() => {
        return {
            statusPanels: [
                {
                    statusPanel: "agAggregationComponent",
                    statusPanelParams: {
                        aggFuncs: ["avg", "sum"],
                    },
                    align: "right",
                },
            ],
        };
    }, []);

    const rowData = useMemo(() => {
        if (!metric?.values) return [];

        const filteredData = metric.values
            .filter((item) => item.value !== null && item.value !== undefined)
            .sort((a, b) => a.period.localeCompare(b.period))
            .slice(-12)
            .reverse();

        if (isRecurringPayment) {
            return filteredData.map((item) => {
                const formattedValue = item.formattedValue || "";
                if (
                    typeof formattedValue === "string" &&
                    formattedValue.includes("(")
                ) {
                    const parts = formattedValue.split("(");
                    const mainValue = parts[0].trim();
                    const detailValue = `(${parts.slice(1).join("(")}`;
                    return {
                        ...item,
                        formattedValue: `${mainValue}\n${detailValue}`,
                    };
                }
                return item;
            });
        }

        return filteredData;
    }, [metric, isRecurringPayment]);

    const descriptionText = useMemo(() => {
        return metric?.description?.description || null;
    }, [metric?.description]);

    const metricName = useMemo(() => {
        return metric?.description?.name || null;
    }, [metric?.description]);

    const resamplingMethod = useMemo(() => {
        return metric?.description?.resamplingMethod || null;
    }, [metric?.description]);

    const fieldType = useMemo(() => {
        return metric?.description?.fieldType || null;
    }, [metric?.description]);

    const localLabel = useMemo(() => {
        return metric?.info?.derived?.localLabel || null;
    }, [metric?.info]);

    const asOfDate = useMemo(() => {
        const dateString = metric?.info?.derived?.asOfDate;
        if (!dateString) return null;

        try {
            const date = new Date(dateString);
            return date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
            });
        } catch {
            return null;
        }
    }, [metric?.info]);

    const hasData = rowData.length > 0;

    return (
        <div className={clsx("bg-default", "flex size-full flex-col gap-4")}>
            <div className="pt-4">
                <RightSidebarButton label="Close" iconDirection="right" />
            </div>
            <div className="size-full">
                <h1 className="pageheading mb-1">{title}</h1>
                {metricName && metricName !== title && (
                    <p className="subdued body-micro mb-2">{metricName}</p>
                )}
                {asOfDate && (
                    <p className="subdued body-micro mb-1">As of {asOfDate}</p>
                )}
                {showDataSources && (
                    <>
                        {resamplingMethod && (
                            <p className="subdued body-micro mb-1">
                                Resampling: {resamplingMethod}
                            </p>
                        )}
                        {fieldType && (
                            <p className="subdued body-micro mb-1">
                                Field type: {fieldType}
                            </p>
                        )}
                        {label && (
                            <p className="subdued body-micro mb-1">
                                Label: {label}
                            </p>
                        )}
                        {localLabel && (
                            <p className="subdued body-micro mb-1">
                                Local label: {localLabel}
                            </p>
                        )}
                    </>
                )}
                {descriptionText && (
                    <p className="subdued mb-2 font-[300] italic">
                        {descriptionText}
                    </p>
                )}
                <div className="mt-6 size-full">
                    {hasData ? (
                        <AgGridReact
                            theme={investmentMemoGridTheme}
                            rowData={rowData}
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            statusBar={statusBar}
                            domLayout="autoHeight"
                        />
                    ) : (
                        <div className="flex items-center justify-center py-12">
                            <p className="subdued body-s">Data not available</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
